import { Radio } from "lucide-react";
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
} from "react";
import { cn } from "./lib";

// A panel surface — white in light mode, translucent zinc in dark.
export function Card({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "rounded-2xl border bg-white p-5 sm:p-6",
        "border-zinc-200 dark:border-zinc-800 dark:bg-zinc-900/60",
        className
      )}
    >
      {children}
    </div>
  );
}

// Solid emerald call-to-action button.
export function PrimaryButton({
  className,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60",
        "bg-emerald-600 text-white hover:bg-emerald-500",
        "dark:bg-emerald-500 dark:text-zinc-950 dark:hover:bg-emerald-400",
        className
      )}
    >
      {children}
    </button>
  );
}

// Bordered, neutral button — used for secondary actions and icon-only controls.
export function GhostButton({
  className,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60",
        "border-zinc-200 text-zinc-700 hover:bg-zinc-100",
        "dark:border-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-800",
        className
      )}
    >
      {children}
    </button>
  );
}

// Square icon-only button (skip, copy, back, etc.).
export function IconButton({
  className,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      className={cn(
        "inline-flex h-10 w-10 items-center justify-center rounded-lg border transition-colors disabled:cursor-not-allowed disabled:opacity-60",
        "border-zinc-200 text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900",
        "dark:border-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-800 dark:hover:text-zinc-100",
        className
      )}
    >
      {children}
    </button>
  );
}

export function TextInput({
  className,
  ...props
}: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={cn(
        "w-full rounded-lg border py-2.5 px-3 text-sm transition-colors focus:outline-none focus:ring-2",
        "border-zinc-200 bg-white text-zinc-900 placeholder-zinc-400 focus:border-emerald-500/50 focus:ring-emerald-500/20",
        "dark:border-zinc-800 dark:bg-zinc-950 dark:text-zinc-100 dark:placeholder-zinc-600",
        className
      )}
    />
  );
}

// Streamwave wordmark + radio badge.
export function Logo({ size = "md" }: { size?: "sm" | "md" }) {
  const badge = size === "sm" ? "h-6 w-6" : "h-7 w-7";
  const icon = size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4";
  return (
    <span className="flex items-center gap-2">
      <span
        className={cn(
          "flex items-center justify-center rounded-md bg-emerald-500 text-zinc-950",
          badge
        )}
      >
        <Radio className={icon} />
      </span>
      <span className="text-sm font-semibold tracking-tight text-zinc-900 dark:text-zinc-100">
        Streamwave
      </span>
    </span>
  );
}

// The pulsing emerald dot used by the "live" and hero badges.
export function PulseDot() {
  return (
    <span className="relative flex h-1.5 w-1.5">
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
    </span>
  );
}
